/**
 * Login Component - Usa Firebase Authentication
 * Soporta: Email/Password, Google Sign-In, Recuperar contraseña
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Collapse,
  Fade,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  Cloud as CloudIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { SiTiktok } from "react-icons/si";
import { StyledDialog, inputStyles } from './common';
import { LoginWaves } from './common/NaraIllustrations';

const API_URL = process.env.REACT_APP_API_URL || "https://turismo.nara.chat/api";

// Partners — same icons/colors as landing page TechStack
const PARTNERS = [
  { name: "OpenAI", icon: AutoAwesomeIcon, color: "#10a37f" },
  { name: "Anthropic", icon: VisibilityIcon, color: "#d4a27f" },
  { name: "WhatsApp", icon: WhatsAppIcon, color: "#25D366" },
  { name: "Twilio", icon: CloudIcon, color: "#f22f46" },
];

// Países y ciudades
const countryOptions = [
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "PA", name: "Panamá" },
];

const citiesByCountry = {
  CO: ["Armenia", "Barranquilla", "Bogotá", "Bucaramanga", "Cali", "Cartagena", "Medellín", "Pereira"],
  MX: ["Ciudad de México", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Mérida", "Querétaro"],
  PA: ["Ciudad de Panamá", "Colón", "David", "Santiago", "Chitré", "La Chorrera"],
};

export default function Login() {
  const navigate = useNavigate();

  // Estados del formulario
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Estados de carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Estados para registro de empresa
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [companyData, setCompanyData] = useState({
    company: "",
    address: "",
    city: "",
    country: "",
    tax_id: "",
  });
  const [tipoRegistro, setTipoRegistro] = useState(null);
  const [nitValidado, setNitValidado] = useState(false);
  const [nitEmpresaNombre, setNitEmpresaNombre] = useState("");
  const [nitError, setNitError] = useState("");

  // Estado para recuperar contraseña
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Verificar si ya está autenticado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && localStorage.getItem("user_id")) {
        navigate("/booking");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Limpiar errores después de 5 segundos
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Autenticar con el backend usando token de Firebase
   */
  const authenticateWithBackend = async (firebaseUser, companyInfo = null) => {
    try {
      const idToken = await firebaseUser.getIdToken();

      const payload = { token: idToken };
      if (companyInfo) {
        payload.company = companyInfo;
      }

      const response = await fetch(`${API_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        // Verificar si el usuario está pendiente de aprobación
        if (data.status === "pending_approval") {
          navigate("/pending-approval", {
            state: { status: "pending_approval", empresa_nombre: data.empresa_nombre, name: data.name }
          });
          return false;
        }
        if (data.status === "rejected") {
          navigate("/pending-approval", {
            state: { status: "rejected", name: data.name }
          });
          return false;
        }
        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("user_email", data.email);
        localStorage.setItem("user_name", data.name);
        localStorage.setItem("user", JSON.stringify({ company_id: data.company_id, role: data.role, name: data.name, email: data.email }));
        // Cargar la zona horaria de la empresa para formateo de fechas
        try {
          const tzRes = await fetch(`${API_URL}/empresa/${data.company_id}/timezone`);
          if (tzRes.ok) {
            const tzData = await tzRes.json();
            if (tzData.timezone) {
              const { setEmpresaTimezone } = await import("../utils/fechaUtils");
              setEmpresaTimezone(tzData.timezone);
            }
          }
        } catch (e) { console.warn('No se pudo cargar timezone empresa:', e); }
        navigate("/booking");
        return true;
      } else if (response.status === 422) {
        // Usuario nuevo, necesita datos de empresa
        setPendingFirebaseUser(firebaseUser);
        setShowCompanyDialog(true);
        return false;
      } else {
        throw new Error(data.detail || "Error de autenticación");
      }
    } catch (err) {
      throw err;
    }
  };

  /**
   * Login con Email y Password usando Firebase
   */
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Intentar login con Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await authenticateWithBackend(userCredential.user);
    } catch (firebaseError) {
      console.error("Firebase error:", firebaseError.code);

      // Si el usuario no existe en Firebase, intentar login legacy
      if (firebaseError.code === "auth/user-not-found" ||
          firebaseError.code === "auth/invalid-credential") {
        try {
          // Intentar con el endpoint legacy
          const response = await fetch(`${API_URL}/login/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });

          if (response.ok) {
            const data = await response.json();
            localStorage.setItem("user_id", data.user_id);
            localStorage.setItem("user", JSON.stringify({ company_id: data.company_id, role: data.role }));

            // Crear usuario en Firebase para futuros logins
            try {
              await createUserWithEmailAndPassword(auth, email, password);
              setSuccess("Cuenta migrada a Firebase exitosamente");
            } catch (createError) {
              console.log("Usuario ya existe en Firebase o error:", createError.code);
            }

            navigate("/booking");
          } else {
            setError("Correo o contraseña incorrectos");
          }
        } catch (legacyError) {
          setError("Correo o contraseña incorrectos");
        }
      } else if (firebaseError.code === "auth/wrong-password") {
        setError("Contraseña incorrecta");
      } else if (firebaseError.code === "auth/too-many-requests") {
        setError("Demasiados intentos. Intenta más tarde o recupera tu contraseña.");
      } else {
        setError("Error al iniciar sesión. Verifica tus credenciales.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login con Google
   */
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(auth, provider);
      await authenticateWithBackend(result.user);
    } catch (err) {
      console.error("Google login error:", err);
      if (err.code === "auth/popup-closed-by-user") {
        setError("Inicio de sesión cancelado");
      } else if (err.code === "auth/popup-blocked") {
        setError("El popup fue bloqueado. Permite popups para este sitio.");
      } else {
        setError(err.message || "Error al iniciar sesión con Google");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Completar registro con datos de empresa
   */
  const verificarNit = async () => {
    const nit = companyData.tax_id.trim();
    if (!nit) { setNitError("Ingresa el NIT"); return; }
    setNitError("");
    setLoading(true);
    try {
      const res = await fetch(API_URL + "/auth/check-nit?tax_id=" + encodeURIComponent(nit));
      const data = await res.json();
      if (data.exists) {
        setNitValidado(true);
        setNitEmpresaNombre(data.empresa_nombre || "");
      } else {
        setNitError("No se encontró una empresa con ese NIT.");
        setNitValidado(false);
      }
    } catch {
      setNitError("Error al verificar.");
    }
    setLoading(false);
  };

  const handleCompanySubmit = async (e) => {
    if (e) e.preventDefault();

    if (tipoRegistro === "nueva") {
      if (!companyData.tax_id || !companyData.company || !companyData.country || !companyData.city) {
        setError("Por favor completa todos los campos obligatorios");
        return;
      }
    } else if (tipoRegistro === "existente") {
      if (!nitValidado) { setError("Verifica el NIT primero"); return; }
    }

    setLoading(true);
    setError("");

    try {
      const data = tipoRegistro === "existente"
        ? { tax_id: companyData.tax_id, company: "", address: "", city: "", country: "" }
        : companyData;
      await authenticateWithBackend(pendingFirebaseUser, data);
      setShowCompanyDialog(false);
    } catch (err) {
      setError(err.message || "Error al registrar empresa");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Recuperar contraseña
   */
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setError("Ingresa tu correo electrónico");
      return;
    }

    setForgotLoading(true);
    setError("");

    try {
      await sendPasswordResetEmail(auth, forgotEmail);
      setForgotSuccess(true);
    } catch (err) {
      console.error("Reset password error:", err);
      if (err.code === "auth/user-not-found") {
        setError("No existe una cuenta con ese correo");
      } else {
        setError("Error al enviar el correo. Intenta de nuevo.");
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const selectedCountryCode = countryOptions.find((c) => c.name === companyData.country)?.code;

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", display: "flex", overflow: "hidden" }}>
      {/* Banner de entorno de pruebas */}
      {process.env.REACT_APP_ENV === 'test' && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          bgcolor: '#f59e0b', color: '#fff', textAlign: 'center',
          py: 0.5, fontSize: 12, fontWeight: 700, letterSpacing: 1,
        }}>
          ENTORNO DE PRUEBAS
        </Box>
      )}

      {/* COLUMNA IZQUIERDA — Branding (solo desktop) */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        width: "45%", minHeight: "100vh", flexDirection: "column",
        justifyContent: "center", alignItems: "center", position: "relative",
        background: "linear-gradient(135deg, #164e63 0%, #0c4a6e 50%, #0891b2 100%)",
        p: 5,
      }}>
        <LoginWaves />
        <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 380 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.1)", mx: "auto", mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 54, height: 54, objectFit: "contain" }} />
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: 32, color: "#fff", lineHeight: 1.2, mb: 1 }}>
            NARA Tourism
          </Typography>
          <Typography sx={{ fontSize: 16, color: "rgba(255,255,255,0.7)", mb: 4, lineHeight: 1.5 }}>
            Reservaciones inteligentes por WhatsApp para operadores turísticos
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 4, textAlign: "left" }}>
            {[
              { icon: "🤖", text: "Chatbot 24/7 que vende pasadías" },
              { icon: "📋", text: "Reservas automáticas con código" },
              { icon: "🌴", text: "Gestión de salidas y ocupación" },
            ].map((item, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 20 }}>{item.icon}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}>{item.text}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", opacity: 0.4 }}>
            {["openai", "twilio", "whatsapp"].map(p => (
              <img key={p} src={`/images/partners/${p}.svg`} alt={p} style={{ height: 20, filter: "brightness(0) invert(1)" }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* COLUMNA DERECHA — Formulario */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#f0fdfa", p: { xs: 2, sm: 4 },
      }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Logo mobile only */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "#164e63", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img src="/images/logo-nara-png.png" alt="Nara" style={{ width: 40, height: 40, objectFit: "contain" }} />
            </Box>
          </Box>

          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 24, color: "#134e4a", mb: 0.5, textAlign: { xs: "center", md: "left" } }}>
            Iniciar sesión
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748b", mb: 3, textAlign: { xs: "center", md: "left" } }}>
            Accede a tu panel de gestión turística
          </Typography>

          {/* Mensajes de error/éxito */}
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          </Collapse>

          <Collapse in={!!success}>
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          </Collapse>

          {/* Formulario de login */}
          <form onSubmit={handleEmailLogin}>
            <Stack spacing={1.5}>
              <TextField
                label="Correo electrónico"
                type="email"
                fullWidth
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#f8fafc",
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                  },
                }}
              />

              <TextField
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                fullWidth
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#f8fafc",
                    "&:hover fieldset": { borderColor: "#6366f1" },
                    "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                  },
                }}
              />

              {/* Link olvidé contraseña */}
              <Box sx={{ textAlign: "right", mt: -1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6366f1",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => {
                    setForgotEmail(email);
                    setShowForgotPassword(true);
                    setForgotSuccess(false);
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Typography>
              </Box>

              {/* Botón login */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #164e63 0%, #0891b2 100%)",
                  boxShadow: "0 4px 14px rgba(30, 58, 95, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0891b2 0%, #164e63 100%)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Iniciar sesión"}
              </Button>
            </Stack>
          </form>

          {/* Divider */}
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="#94a3b8">
              o continúa con
            </Typography>
          </Divider>

          {/* Botón Google */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 14,
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#334155",
              "&:hover": {
                borderColor: "#6366f1",
                background: "#f8fafc",
              },
            }}
          >
            Google
          </Button>

          {/* Link registro */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2" color="#64748b" sx={{ fontSize: 13 }}>
              ¿No tienes cuenta?{" "}
              <Typography
                component="span"
                sx={{
                  color: "#6366f1",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/register")}
              >
                Regístrate
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dialog: Datos de empresa */}
      <StyledDialog
        open={showCompanyDialog}
        onClose={() => { if (!loading) { setShowCompanyDialog(false); setTipoRegistro(null); setNitValidado(false); setNitError(""); } }}
        title={!tipoRegistro ? "¿Tu empresa ya está en NARA?" : tipoRegistro === "nueva" ? "Registrar empresa nueva" : "Unirme a empresa existente"}
        icon={<BusinessIcon />}
      >
        <StyledDialog.Body>
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 1 }}>{error}</Alert>
          </Collapse>

          {/* Paso 1: elegir tipo */}
          {!tipoRegistro && (
            <Stack spacing={2}>
              <Button fullWidth variant="outlined" onClick={() => setTipoRegistro("nueva")}
                sx={{ py: 2, borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#334155" }}>
                <Box sx={{ textAlign: "center" }}>
                  <BusinessIcon sx={{ fontSize: 28, color: "#10b981", mb: 0.5 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Empresa nueva</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Registrar una empresa por primera vez</Typography>
                </Box>
              </Button>
              <Button fullWidth variant="outlined" onClick={() => setTipoRegistro("existente")}
                sx={{ py: 2, borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "#334155" }}>
                <Box sx={{ textAlign: "center" }}>
                  <PersonIcon sx={{ fontSize: 28, color: "#3b82f6", mb: 0.5 }} />
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Empresa existente</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Unirme a una empresa ya registrada</Typography>
                </Box>
              </Button>
            </Stack>
          )}

          {/* Paso 2a: empresa existente — solo NIT */}
          {tipoRegistro === "existente" && (
            <Stack spacing={2}>
              <Typography sx={{ color: "#334155", fontSize: 13, fontWeight: 600 }}>
                Ingresa el NIT de la empresa
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField label="NIT / RFC / RUT" value={companyData.tax_id} size="small" fullWidth
                  onChange={(e) => { setCompanyData({ ...companyData, tax_id: e.target.value }); setNitValidado(false); setNitError(""); }}
                  disabled={loading || nitValidado} sx={inputStyles} />
                {!nitValidado && (
                  <Button variant="contained" onClick={verificarNit} disabled={loading || !companyData.tax_id.trim()}
                    sx={{ minWidth: 100, borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" }}>
                    Verificar
                  </Button>
                )}
              </Box>
              {nitError && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>{nitError}</Alert>}
              {nitValidado && (
                <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>
                  Empresa encontrada: <strong>{nitEmpresaNombre}</strong>.
                  Se enviará solicitud de acceso al administrador.
                </Alert>
              )}
            </Stack>
          )}

          {/* Paso 2b: empresa nueva — todos los datos */}
          {tipoRegistro === "nueva" && (
            <form onSubmit={handleCompanySubmit}>
              <Stack spacing={2}>
                <TextField label="NIT / RFC / RUT" value={companyData.tax_id} size="small" fullWidth required
                  onChange={(e) => setCompanyData({ ...companyData, tax_id: e.target.value })} sx={inputStyles} />
                <TextField label="Nombre de la empresa" value={companyData.company} size="small" fullWidth required
                  onChange={(e) => setCompanyData({ ...companyData, company: e.target.value })} sx={inputStyles} />
                <TextField label="Dirección" value={companyData.address} size="small" fullWidth
                  onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })} sx={inputStyles} />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField select label="País" value={companyData.country} size="small" fullWidth required
                    onChange={(e) => setCompanyData({ ...companyData, country: e.target.value, city: "" })}
                    SelectProps={{ native: true }} sx={inputStyles}>
                    <option value="">Seleccionar</option>
                    {countryOptions.map((c) => (<option key={c.code} value={c.name}>{c.name}</option>))}
                  </TextField>
                  <TextField select label="Ciudad" value={companyData.city} size="small" fullWidth required
                    onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                    disabled={!companyData.country} SelectProps={{ native: true }} sx={inputStyles}>
                    <option value="">Seleccionar</option>
                    {(citiesByCountry[selectedCountryCode] || []).map((city) => (<option key={city} value={city}>{city}</option>))}
                  </TextField>
                </Box>
              </Stack>
            </form>
          )}
        </StyledDialog.Body>

        {/* Botones — solo si eligió tipo */}
        {tipoRegistro && (
          <StyledDialog.Actions>
            <StyledDialog.CancelButton
              onClick={() => { setTipoRegistro(null); setNitValidado(false); setNitError(""); setError(""); }}
              disabled={loading}
            />
            <StyledDialog.PrimaryButton
              onClick={handleCompanySubmit}
              loading={loading}
              disabled={tipoRegistro === "existente" && !nitValidado}
            >
              {tipoRegistro === "existente" ? "Solicitar acceso" : "Registrar"}
            </StyledDialog.PrimaryButton>
          </StyledDialog.Actions>
        )}
      </StyledDialog>

      {/* Dialog: Recuperar contraseña */}
      <StyledDialog
        open={showForgotPassword}
        onClose={() => !forgotLoading && setShowForgotPassword(false)}
        title="Recuperar contraseña"
        icon={<LockIcon />}
        variant="compact"
        maxWidth="xs"
      >
        {forgotSuccess ? (
          <StyledDialog.Body compact>
            <Box sx={{ textAlign: "center", py: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: "#10b981", mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, color: "#155e75", fontSize: 16 }}>
                ¡Correo enviado!
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.5 }}>
                Revisa tu bandeja de entrada y sigue las instrucciones para restablecer tu contraseña.
              </Typography>
            </Box>
          </StyledDialog.Body>
        ) : (
          <StyledDialog.Body compact>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
            </Typography>

            <Collapse in={!!error}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            </Collapse>

            <TextField
              label="Correo electrónico"
              type="email"
              fullWidth
              size="small"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotLoading}
              autoFocus
              sx={inputStyles}
            />
          </StyledDialog.Body>
        )}
        <StyledDialog.CompactActions>
          {forgotSuccess ? (
            <StyledDialog.PrimaryButton
              fullWidth
              onClick={() => setShowForgotPassword(false)}
            >
              Entendido
            </StyledDialog.PrimaryButton>
          ) : (
            <>
              <StyledDialog.CancelButton
                fullWidth
                onClick={() => setShowForgotPassword(false)}
                disabled={forgotLoading}
              />
              <StyledDialog.PrimaryButton
                fullWidth
                onClick={handleForgotPassword}
                disabled={!forgotEmail}
                loading={forgotLoading}
              >
                Enviar
              </StyledDialog.PrimaryButton>
            </>
          )}
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}
